import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { CalSlot } from '@/types/scheduling';
import { formatSlotTime } from '@/services/integrations/cal';
import { Loader2, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

// Format une tranche horaire (début - fin)
const formatTimeRange = (start: string, end: string): string => {
  return `${formatSlotTime(start)} - ${formatSlotTime(end)}`;
};

// Plans data
const plansData = {
  'presence': {
    label: 'Présence',
    subtitle: '6 mois - 4 contenus/mois',
    price: '1 000€ HT/mois',
    total: '6 000€ HT',
    features: [
      '4 vidéos diffusées par mois',
      '24 vidéos au total sur 6 mois',
      'Stratégie de contenu sur le long terme',
      'Conciergerie & Support premium'
    ]
  },
  'emergence': {
    label: 'Émergence',
    subtitle: '2 mois - 10 contenus/mois',
    price: '2 500€ HT/mois',
    total: '5 000€ HT',
    features: [
      '10 vidéos diffusées par mois',
      '20 vidéos au total sur 2 mois',
      'Stratégie de lancement rapide',
      'Conciergerie & Support premium'
    ]
  },
  'acceleration': {
    label: 'Accélération',
    subtitle: '1 mois - 20 contenus/mois',
    price: '5 000€ HT',
    total: '5 000€ HT',
    features: [
      '20 vidéos diffusées en 1 mois',
      '5 vidéos par semaine',
      'Stratégie de croissance accélérée',
      'Conciergerie & Support premium'
    ]
  }
};

/**
 * Props du composant BookingConfirmation
 */
interface BookingConfirmationProps {
  /** État d'ouverture du modal */
  isOpen: boolean;
  /** Callback de fermeture */
  onClose: () => void;
  /** Créneau sélectionné */
  selectedSlot?: CalSlot;
  /** Date sélectionnée */
  selectedDate?: Date;
  /** Clé du plan sélectionné */
  planKey?: string;
  /** Nom de la localisation */
  locationName?: string;
  /** CGV acceptées */
  acceptedCGV: boolean;
  /** Callback pour accepter les CGV */
  onAcceptCGV: (accepted: boolean) => void;
  /** Callback pour ouvrir les CGV */
  onOpenCGV: () => void;
  /** Callback pour confirmer la réservation */
  onConfirm: (withMultiHook: boolean) => void;
  /** Indicateur de chargement */
  isBooking?: boolean;
  /** Afficher le warning CGV */
  showCGVWarning?: boolean;
  /** Indicateur de quelle réservation est en cours (null | false | true) */
  bookingWithMultiHook?: boolean | null;
}

/**
 * Modal de confirmation de réservation
 * 
 * Affiche :
 * - Récapitulatif de la réservation (date, heure, plan, lieu)
 * - Checkbox d'acceptation des CGV
 * - Lien vers les CGV
 * - Boutons de confirmation et annulation
 * - Warning si CGV non acceptées
 * 
 * @example
 * <BookingConfirmation
 *   isOpen={showConfirmDialog}
 *   onClose={() => setShowConfirmDialog(false)}
 *   selectedSlot={selectedSlot}
 *   selectedDate={selectedDate}
 *   planName={planName}
 *   locationName={locationName}
 *   acceptedCGV={acceptedCGV}
 *   onAcceptCGV={setAcceptedCGV}
 *   onOpenCGV={() => setShowCGVDialog(true)}
 *   onConfirm={handleConfirm}
 *   isBooking={isBooking}
 * />
 */
export function BookingConfirmation({
  isOpen,
  onClose,
  selectedSlot,
  selectedDate,
  planKey,
  locationName,
  acceptedCGV,
  onAcceptCGV,
  onOpenCGV,
  onConfirm,
  isBooking = false,
  showCGVWarning = false,
  bookingWithMultiHook = null,
}: Readonly<BookingConfirmationProps>) {
  const formattedDate = selectedDate?.toLocaleDateString('fr-FR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const formattedTimeRange = selectedSlot ? formatTimeRange(selectedSlot.start, selectedSlot.end) : '';
  const planData = planKey ? plansData[planKey as keyof typeof plansData] : null;

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        onClose();
        if (!open) {
          onAcceptCGV(false);
        }
      }}
    >
      <DialogContent 
        className={cn(
          "max-w-full w-full h-full pl-0 pr-0 pb-0 border-0 bg-black z-[60] flex flex-col",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "pt-[env(safe-area-inset-top)]"
        )}
      >
        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto px-6 pt-6 pb-4">
          <div className="max-w-lg mx-auto space-y-6">
            <DialogHeader className="mb-6 text-center">
              <DialogTitle className="text-2xl font-semibold text-white">
                Confirmer la réservation
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Plan sélectionné */}
              {planData && (
                <div className="glass rounded-3xl p-6">
                  {/* Header plan */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-1">
                        {planData.label}
                      </h3>
                      <p className="text-sm text-white/60">
                        {planData.subtitle}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-base font-semibold text-white">
                        {planData.price}
                      </div>
                      <div className="text-xs text-white/60 mt-1">
                        Total {planData.total}
                      </div>
                    </div>
                  </div>

                  <Separator className="bg-white/10 my-4" />

                  {/* Features */}
                  <div className="space-y-3">
                    {planData.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        <span className="text-white/80 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Récapitulatif */}
              <div className="glass rounded-3xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Récapitulatif</h3>
                
                <div className="space-y-4">
                  {/* Date */}
                  <div className="flex items-center justify-between">
                    <span className="text-white/60 text-sm">Date</span>
                    <span className="text-white font-medium text-sm capitalize">{formattedDate}</span>
                  </div>

                  {/* Créneau horaire */}
                  <div className="flex items-center justify-between">
                    <span className="text-white/60 text-sm">Créneau horaire</span>
                    <span className="text-white font-medium text-sm">{formattedTimeRange}</span>
                  </div>

                  {/* Localisation */}
                  {locationName && (
                    <div className="flex items-center justify-between">
                      <span className="text-white/60 text-sm">Localisation</span>
                      <span className="text-white font-medium text-sm">{locationName}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer fixe avec CGV et bouton */}
        <div className="border-t border-white/10 bg-black/95 backdrop-blur-xl p-6">
          <div className="space-y-4 max-w-lg mx-auto">
            {/* CGV */}
            <div className="flex items-center gap-3">
              <Checkbox
                id="cgv-confirm"
                checked={acceptedCGV}
                onCheckedChange={(checked) => onAcceptCGV(checked === true)}
                className="h-6 w-6 border-2 border-white/40 data-[state=checked]:bg-white data-[state=checked]:border-white data-[state=checked]:text-black"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onOpenCGV();
                }}
                className={cn(
                  "flex-1 text-left text-sm leading-relaxed transition-colors underline hover:text-white/80",
                  showCGVWarning && !acceptedCGV ? "text-red-400" : "text-white/60"
                )}
              >
                J'accepte les CGV
              </button>
            </div>

            {showCGVWarning && !acceptedCGV && (
              <div className="text-xs text-red-400 bg-red-500/10 border border-red-400/20 rounded-xl p-3">
                Veuillez accepter les CGV pour continuer
              </div>
            )}

            <button
              onClick={() => onConfirm(false)}
              disabled={isBooking}
              className={cn(
                "w-full rounded-2xl px-8 py-4 text-white font-medium transition-all",
                "bg-white/10 hover:bg-white/15 active:scale-95",
                "disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              )}
            >
              {isBooking ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Confirmation...</span>
                </>
              ) : (
                'Continuer'
              )}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
